<template>
  <div
    class="flex flex-col p-6 bg-white rounded-lg shadow-sm border transition-all duration-200"
    :class="[
      isSelected ? 'border-primary-purple border-2' : 'border-gray-200 border',
    ]"
  >
    <h3 class="text-xl font-semibold mb-4">{{ plan.name }}</h3>
    <div class="text-2xl font-bold mb-4">${{ monthlyCharge(plan) }}/mo</div>
    <div class="text-sm text-gray-600 mb-4">
      {{ plan.payoutFeePercentage }}% payout fee
    </div>
    <ul class="space-y-3 mb-6">
      <li
        v-for="feature in plan.features"
        :key="feature"
        class="flex items-start text-left"
      >
        <span class="text-green-500 mr-2">✓</span>
        <span class="text-gray-700">{{ feature }}</span>
      </li>
    </ul>
    <BaseButton
      class="w-full mt-auto py-2 rounded-md transition-colors duration-200"
      :class="[
        isSelected
          ? 'bg-primary-purple text-white hover:bg-purple-700'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      ]"
      @click="handleButtonClick"
    >
      {{ buttonText }}
    </BaseButton>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import BaseButton from "@/components/shared/BaseButton.vue";
import { SubscriptionPlanInfo } from "@/types/subscription-types";

export default defineComponent({
  name: "SubscriptionPlanCard",
  components: {
    BaseButton,
  },
  props: {
    plan: {
      type: Object as PropType<SubscriptionPlanInfo>,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    customAmount: {
      type: Number as PropType<number | null>,
      default: null,
    },
    currentAmount: {
      type: Number,
      required: true,
    },
  },
  emits: ["select", "contactSupport", "viewPaymentMethod"],
  setup(props, { emit }) {
    const monthlyCharge = (plan: SubscriptionPlanInfo) => {
      if (plan.billingCycle === "monthly") {
        return showPlanAmount(plan);
      } else if (plan.billingCycle === "yearly") {
        return showPlanAmount(plan) / 12;
      }
    };

    const showPlanAmount = (plan: SubscriptionPlanInfo) => {
      if (props.customAmount && props.isSelected) {
        return props.customAmount;
      } else {
        return plan.amount;
      }
    };

    const isDowngrade = computed(() => {
      const planMonthlyAmount =
        props.plan.billingCycle === "yearly"
          ? props.plan.amount / 12
          : props.plan.amount;

      const currentMonthlyAmount = props.currentAmount;

      return planMonthlyAmount < currentMonthlyAmount;
    });

    const buttonText = computed(() => {
      if (props.isSelected) {
        return "Current Plan";
      }
      if (isDowngrade.value) {
        return "Contact Support to Downgrade";
      }
      return "Select Plan";
    });

    const handleButtonClick = () => {
      if (props.isSelected) {
        emit("viewPaymentMethod", props.plan.subscriptionPlanTierId);
        return;
      }
      if (isDowngrade.value) {
        emit("contactSupport");
      } else {
        emit("select", props.plan.subscriptionPlanTierId);
      }
    };

    return { monthlyCharge, buttonText, handleButtonClick, isDowngrade };
  },
});
</script>
